import { motion } from "framer-motion";
import { Reveal, MaskedLines } from "@/components/site/Reveal";
import { AgentVoiceCard, PersonaIcon } from "@/components/site/AgentVoiceCard";
import { LeadPopup } from "@/components/site/LeadPopup";
import { AGENT_DEMOS } from "@/data/pageCopy";


const { kicker, h1Lines, lead, personas } = AGENT_DEMOS;

export default function AgentDemosPage() {
  return (
    <div data-testid="agent-demos-page">
      <LeadPopup context="agents" />
      <section className="relative pt-32 md:pt-40 pb-20 overflow-hidden bg-[#0a0a0a]">
        <div className="pointer-events-none absolute inset-0 opacity-[0.05]" style={{
          backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
          backgroundSize: "64px 64px",
        }} />

        {/* warm glow behind the headline, same treatment as the main Hero */}
        <div
          className="pointer-events-none absolute left-1/2 top-0 -translate-x-1/2 w-[1100px] h-[600px]"
          style={{
            background: "radial-gradient(ellipse 50% 50% at 50% 0%, rgba(0,71,255,0.35), rgba(0,71,255,0) 70%)",
            filter: "blur(40px)",
          }}
        />
        <div
          className="pointer-events-none absolute left-1/2 top-[280px] -translate-x-1/2 w-[900px] h-[380px]"
          style={{
            background: "radial-gradient(ellipse 50% 50% at 50% 100%, rgba(255,130,40,0.18), rgba(255,130,40,0) 70%)",
            filter: "blur(50px)",
          }}
        />

        <div className="relative mx-auto max-w-[1400px] px-6 md:px-12">
          <Reveal><p className="font-mono-t text-xs uppercase tracking-[0.25em] text-[#5B8CFF] mb-5">{kicker}</p></Reveal>
          <h1 className="font-heading font-medium tracking-tight leading-[0.98] text-4xl md:text-6xl lg:text-7xl text-white max-w-4xl">
            <MaskedLines lines={h1Lines} delay={0.1} />
          </h1>
          <Reveal delay={0.35}>
            <p className="mt-8 text-lg text-neutral-400 max-w-2xl leading-relaxed">
              {lead}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-20 bg-[#0a0a0a] border-t border-white/10">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {personas.map((p, i) => (
              <Reveal key={p.agentId} delay={(i % 3) * 0.06}>
                <motion.div
                  whileHover={{ y: -4, borderColor: "rgba(91,140,255,0.4)" }}
                  transition={{ type: "spring", stiffness: 300, damping: 24 }}
                  className="relative h-full rounded-xl border border-white/10 bg-[#0d0d0f] p-9 flex flex-col overflow-hidden"
                  data-testid={`agent-demo-${p.agentId}`}
                >
                  {/* warm amber glow, Launch-UI style — lit from the card's own top edge */}
                  <div
                    className="pointer-events-none absolute inset-0"
                    style={{
                      background: "radial-gradient(ellipse 90% 55% at 50% 0%, rgba(255,150,40,0.4), rgba(255,150,40,0) 70%)",
                    }}
                  />

                  <div className="relative flex items-center justify-between">
                    <PersonaIcon icon={p.icon} size={30} />
                    <span className="flex items-center gap-1.5 font-mono-t text-[9px] uppercase tracking-widest text-neutral-500">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#5B8CFF] animate-pulse" /> Live
                    </span>
                  </div>
                  <h3 className="relative mt-6 font-heading font-semibold text-xl text-white">{p.t}</h3>
                  <p className="relative mt-2.5 text-neutral-400 text-sm leading-relaxed">{p.d}</p>
                  <p className="relative mt-3 font-mono-t text-[10px] uppercase tracking-widest text-neutral-600">
                    Roleplay · {p.fiction}
                  </p>
                  <div className="relative mt-6 pt-6 border-t border-white/10">
                    <AgentVoiceCard agentId={p.agentId} label={p.label} greeting={p.greeting} />
                  </div>
                </motion.div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <p className="mt-8 text-sm text-neutral-500 max-w-xl">
              Want an agent trained on your own business instead of a demo persona?{" "}
              <a href="/demo" className="text-[#5B8CFF] hover:underline">Book a demo</a> and we'll build it with you.
            </p>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
